if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
        if (optionValues.length) params.push(`option_values=${optionValues.join(',')}`);
        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        // Update Shopify variant input values
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');

        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // Add new items
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // Remove old items
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // Sort to match destination order
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destItem, destIndex) => {
            const sourceData = sourceMap.get(destItem.dataset.mediaId);
            if (sourceData && sourceData.index !== destIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destIndex + 1})`)
              );
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;
        if (max) this.quantityInput.max = max;
        else this.quantityInput.removeAttribute('max');
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            ['data-cart-quantity', 'data-min', 'data-max', 'step'].forEach((attr) => {
              const valUpdated = updated.getAttribute(attr);
              if (valUpdated !== null) current.setAttribute(attr, valUpdated);
              else current.removeAttribute(attr);
            });
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

// // =======================
// // GLOBAL QUANTITY / VARIANT LOGIC
// // =======================
// var $toggle = $("#toggleEye");

// function updateQtyPriceAndVariant(setByVariant = false, isInitialLoad = false) {
//     var $selectedRadio = $('input[type="radio"]:checked');
//     if (!$selectedRadio.length) return;

//     var data_pack = $selectedRadio.attr("data-pack") || "";
//     var data_varid= $selectedRadio.attr("data-varid");
//     var data_packlense = parseInt($selectedRadio.attr("daa-lense-pack")) || 0; // ✅ fixed attribute (no space)
//     var data_quant = parseInt($selectedRadio.attr("data-quanval")) || 1;
//     var pricing = parseFloat($selectedRadio.attr("data-prices")) || 0;
//     var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) ? Shopify.currency.active : "AED";
// $(".custom-variant-id").val(data_varid);
//     // ===== CASE 1: Variant clicked OR initial load → update quantities =====
//     if (setByVariant || isInitialLoad) {
//         if ($toggle.is(":checked")) {
//             // Split Eyes Mode
//             if (data_pack === "3 Pack" || data_pack === "6 Pack" || data_pack === "30 Pack" || data_pack === "90 Pack" ) {
//                $("#left_qty_select").val(Math.ceil(data_packlense / 2));
//         $("#right_qty_select").val(Math.floor(data_packlense / 2));
//             } else {
//                 $("#left_qty_select, #right_qty_select").val(data_packlense);
//             }
//         } else {
//             // Both Eyes Mode
//                   if (data_pack === "3 Pack" || data_pack === "6 Pack" || data_pack === "30 Pack" || data_pack === "90 Pack" ) {
//                 $("#both_qty_select").val(data_packlense);
//             } else {
//                 $("#both_qty_select").val(data_packlense);
//             }
//         }
//     }

//     // ===== CASE 2: Quantity changed → redirect variant =====
//     var totalQty = $toggle.is(":checked")
//         ? (parseInt($("#left_qty_select").val()) || 0) + (parseInt($("#right_qty_select").val()) || 0)
//         : parseInt($("#both_qty_select").val()) || 0;

//     if (!setByVariant && !isInitialLoad) {
//         let redirected = false;

//         // --- Redirect for 3-Pack ---
//         if (data_pack === "3 Pack") {
//             $(".custom__var-label").each(function() {
//                 var label = $(this).text().trim();
//                 var variantInput = $(this).prev("input[type=radio]");

//                 if (totalQty >= 1 && totalQty <= 3 && label.includes("3 Month Supply")) {
//                     variantInput.prop("checked", true).trigger("click");
//                     redirected = true;
//                     return false;
//                 } else if (totalQty >= 6 && label.includes("6 Month Supply")) {
//                     variantInput.prop("checked", true).trigger("click");
//                     redirected = true;
//                     return false;
//                 }
//             });
//         }
//         // --- Redirect for 6-Pack ---
// if (!redirected && data_pack === "6 Pack") {
//     var supplies = [];
//     $(".custom__var-label").each(function() {
//         var label = $(this).text().trim();
//         if (label.includes("3 Month Supply")) supplies.push("3");
//         if (label.includes("6 Month Supply")) supplies.push("6");
//         if (label.includes("12 Month Supply")) supplies.push("12");
//     });

//     $(".custom__var-label").each(function() {
//         var label = $(this).text().trim();
//         var variantInput = $(this).prev("input[type=radio]");

//         if (totalQty >= 1 && totalQty <= 3) {
//             // Pick the smallest available supply
//             if (label.includes("3 Month Supply")) {
//                 variantInput.prop("checked", true);
//                 redirected = true;
//                 return false;
//             } else if (label.includes("6 Month Supply") && !supplies.includes("3")) {
//                 variantInput.prop("checked", true);
//                 redirected = true;
//                 return false;
//             }
//         } else if (totalQty >= 4 && totalQty <= 15) {
//             // Pick the largest available supply
//             if (label.includes("12 Month Supply")) {
//                 variantInput.prop("checked", true);
//                 redirected = true;
//                 return false;
//             } else if (label.includes("6 Month Supply") && !supplies.includes("12")) {
//                 variantInput.prop("checked", true);
//                 redirected = true;
//                 return false;
//             }
//         }
//     });
// }


//         // --- Redirect for 30 / 90 Pack ---
//   // --- Redirect for 30-Pack ---
// if (!redirected && data_pack === "30 Pack") {
//     $(".custom__var-label").each(function() {
//         var variantQty = parseInt($(this).attr("data-quant")) || 0;
//         var variantInput = $(this).prev("input[type=radio]");

//         if (totalQty >= 1 && totalQty <= 5 && variantQty === 2) {
//             variantInput.prop("checked", true);
//             redirected = true;
//             return false;
//         } else if (totalQty >= 6 && totalQty <= 11 && variantQty === 6) {
//             variantInput.prop("checked", true);
//             redirected = true;
//             return false;
//         } else if (totalQty >= 12 && variantQty === 12) {
//             variantInput.prop("checked", true);
//             redirected = true;
//             return false;
//         }
//     });
// }

// // --- Redirect for 90-Pack ---
// if (!redirected && data_pack === "90 Pack") {
//     $(".custom__var-label").each(function() {
//         var variantQty = parseInt($(this).attr("data-quant")) || 0;
//         console.log("variantQty",variantQty);
//         var variantInput = $(this).prev("input[type=radio]");

//         // 1–3 → 3 Month Supply (60 lenses)
//         if (totalQty >= 1 && totalQty <= 3 && variantQty === 6) {
//             variantInput.prop("checked", true);
//             redirected = true;
//             return false;
//         } 
//         // 4+ → 6 Month Supply (90 lenses)
//         else if (totalQty >= 4 && variantQty === 12) {
//             variantInput.prop("checked", true);
//             redirected = true;
//             return false;
//         }
//     });
// }


//         // After redirect → fetch new variant data again
//         if (redirected) {
//             $selectedRadio = $('input[type="radio"]:checked');
//             data_packlense = parseInt($selectedRadio.attr("daa-lense-pack")) || 0;
//             data_quant = parseInt($selectedRadio.attr("data-quanval")) || 1;
//             pricing = parseFloat($selectedRadio.attr("data-prices")) || 0;
//         }
//     }

//     // ===== Update price always =====
//   //  $(".custom_price").text((totalQty * pricing).toFixed(2) + " " + currency);
// }

// // ===== Event Listeners =====
// $toggle.on("change", function() {
//     updateQtyPriceAndVariant(true);
// });

// $("#both_qty_select, #left_qty_select, #right_qty_select").on("change", function() {
//     updateQtyPriceAndVariant(false);
// });

// $(".custom__var-label").on("click", function() {
//     $(this).prev("input[type=radio]").prop("checked", true);
//     updateQtyPriceAndVariant(true);
// });

// // ===== Initial Load =====
// $(document).ready(function() {
//     updateQtyPriceAndVariant(true, true);
// });








// // =======================
// // ARABIC VARIANT + QTY LOGIC (30 PACK ONLY)
// // =======================

// var $toggle = $("#toggleEye");

// function updateVariantByQty(isVariantClick = false, isInitial = false) {
//     var $selected = $('input.custom__variants:checked');
//     if (!$selected.length) return;

//     var lensesPerPack = parseInt($selected.attr("daa-lense-pack")) || 0;

//     // -----------------------
//     // SET QTY ON VARIANT CLICK
//     // -----------------------
//     if (isVariantClick || isInitial) {
//         if ($toggle.is(":checked")) {
//             // Split Eyes
//             $("#left_qty_select").val(Math.ceil(lensesPerPack / 2));
//             $("#right_qty_select").val(Math.floor(lensesPerPack / 2));
//         } else {
//             // Both Eyes
//             $("#both_qty_select").val(lensesPerPack);
//         }
//     }

//     // -----------------------
//     // CALCULATE TOTAL QTY
//     // -----------------------
//     var totalQty = $toggle.is(":checked")
//         ? (parseInt($("#left_qty_select").val()) || 0) +
//           (parseInt($("#right_qty_select").val()) || 0)
//         : parseInt($("#both_qty_select").val()) || 0;

//     if (isVariantClick || isInitial) return;

//     // -----------------------
//     // REDIRECT VARIANT (30 PACK)
//     // -----------------------
//     $(".custom__var-label").each(function () {
//         var variantQty = parseInt($(this).attr("data-quant")) || 0;
//         var $radio = $(this).prev("input[type=radio]");

//         // شهر واحد (60 عدسة)
//         if (totalQty <= 2 && variantQty === 30) {
//             $radio.prop("checked", true).trigger("click");
//             return false;
//         }

//         // 3 أشهر (180 عدسة)
//         if (totalQty >= 3 && totalQty <= 6 && variantQty === 90) {
//             $radio.prop("checked", true).trigger("click");
//             return false;
//         }

//         // 6 أشهر (360 عدسة)
//         if (totalQty >= 7 && variantQty === 180) {
//             $radio.prop("checked", true).trigger("click");
//             return false;
//         }
//     });
// }

// // -----------------------
// // EVENTS
// // -----------------------
// $("#toggleEye").on("change", function () {
//     updateVariantByQty(true);
// });

// $("#both_qty_select, #left_qty_select, #right_qty_select").on("change", function () {
//     updateVariantByQty(false);
// });

// $(".custom__var-label").on("click", function () {
//     $(this).prev("input[type=radio]").prop("checked", true);
//     updateVariantByQty(true);
// });

// // -----------------------
// // INITIAL LOAD
// // -----------------------
// $(document).ready(function () {
//     updateVariantByQty(true, true);
// });




// =======================
// GLOBAL QUANTITY / VARIANT LOGIC
// =======================

$(document).ready(function () {

var $toggle = $("#toggleEye");

// -----------------------
// MAIN VARIANT + QTY LOGIC
// -----------------------

function updateQtyPriceAndVariant(setByVariant = false, isInitialLoad = false) {

    var $selectedRadio = $('input.custom__variants:checked');
    if (!$selectedRadio.length) return;

    var data_pack = $selectedRadio.attr("data-pack") || "";
    var packMatch = data_pack.match(/\d+/);
    var packSize = packMatch ? parseInt(packMatch[0]) : 0;

    var data_varid = $selectedRadio.attr("data-varid");
    var packLense = parseInt($selectedRadio.attr("daa-lense-pack")) || 0;

    $(".custom-variant-id").val(data_varid);

    // ---------- SET QTY ON VARIANT ----------
    if (setByVariant || isInitialLoad) {

        if ($toggle.is(":checked")) {

            if ([3,6,30,90,180].includes(packSize)) {
                $("#left_qty_select").val(Math.ceil(packLense / 2));
                $("#right_qty_select").val(Math.floor(packLense / 2));
            } else {
                $("#left_qty_select, #right_qty_select").val(packLense);
            }

        } else {
            $("#both_qty_select").val(packLense);
        }
    }

    // ---------- TOTAL QTY ----------
    var totalQty = $toggle.is(":checked")
        ? (parseInt($("#left_qty_select").val()) || 0) +
          (parseInt($("#right_qty_select").val()) || 0)
        : (parseInt($("#both_qty_select").val()) || 0);

    if (!setByVariant && !isInitialLoad) {

        let redirected = false;

        function selectVariant(radio){
            radio.prop("checked", true);
            radio.trigger("change");
            radio.trigger("click"); // keep Shopify compatibility
            redirected = true;
        }

        // ---------- HELPERS ----------
        function redirectByLabel(qty, rules) {

            $(".custom__var-label").each(function () {

                if (redirected) return false;

                let label = $(this).text();
                let radio = $(this).prev("input[type=radio]");

                for (let rule of rules) {

                    let matchMin = !rule.min || qty >= rule.min;
                    let matchMax = !rule.max || qty <= rule.max;

                    if (matchMin && matchMax && rule.label.test(label)) {
                        selectVariant(radio);
                        return false;
                    }
                }
            });
        }

        function redirectByQuant(qty, rules) {

            $(".custom__var-label").each(function () {

                if (redirected) return false;

                let variantQty = parseInt($(this).attr("data-quant")) || 0;
                let radio = $(this).prev("input[type=radio]");

                for (let rule of rules) {

                    let matchMin = !rule.min || qty >= rule.min;
                    let matchMax = !rule.max || qty <= rule.max;

                    if (matchMin && matchMax && variantQty === rule.quant) {
                        selectVariant(radio);
                        return false;
                    }
                }
            });
        }

        // ---------- 3 PACK ----------
        if (!redirected && packSize === 3) {
            redirectByLabel(totalQty, [
                { max: 3, label: /3.*(Month|أشهر)/i },
                { min: 6, label: /6.*(Month|أشهر)/i }
            ]);
        }

// ---------- 6 PACK ----------
if (!redirected && packSize === 6) {

    redirectByQuant(totalQty, [
        { max: 3, quant: 6 },   // 1–3 → 6 Month
        { min: 4, quant: 12 }   // 4+ → 12 Month
    ]);

}


// ---------- 12 PACK ----------
if (!redirected && packSize === 12) {

    redirectByQuant(totalQty, [
        { max: 6, quant: 6 },   // 1–6 → 6 Month
        { min: 7, quant: 12 }   // 7+ → 12 Month
    ]);

}




        // ---------- 30 PACK (ORIGINAL LOGIC RESTORED) ----------
        if (!redirected && packSize === 30) {
            redirectByQuant(totalQty, [
                { max: 5, quant: 30 },
                { min: 6, max: 11, quant: 90 },
                { min: 12, quant: 180 }
            ]);
        }

        // ---------- 90 PACK ----------
        if (!redirected && packSize === 90) {

            $(".custom__var-label").each(function() {

                if (redirected) return false;

                var variantQty = parseInt($(this).attr("data-quant")) || 0;
                var radio = $(this).prev("input[type=radio]");

                if (totalQty >= 1 && totalQty <= 3 && variantQty === 90) {
                    selectVariant(radio);
                    return false;
                }

                if (totalQty >= 4 && variantQty === 180) {
                    selectVariant(radio);
                    return false;
                }
            });
        }

        // ---------- 180 PACK ----------
        if (!redirected && packSize === 180) {
            redirectByQuant(totalQty, [
                { max: 3, quant: 180 },
                { min: 4, quant: 360 }
            ]);
        }
    }
}

// -----------------------
// EVENTS
// -----------------------

$toggle.on("change", function () {
    updateQtyPriceAndVariant(true);
});

$("#both_qty_select, #left_qty_select, #right_qty_select").on("change", function () {
    updateQtyPriceAndVariant(false);
});

// IMPORTANT → restore original behavior
$(".custom__var-label").on("click", function () {

    var radio = $(this).prev("input[type=radio]");
    radio.prop("checked", true);

    updateQtyPriceAndVariant(true);
});

updateQtyPriceAndVariant(true, true);


// =======================
// TOGGLE + CART
// =======================

var $both_sph = $("#both_sph_select");
var $both_qty = $("#both_qty_select");

var $left_sph = $("#left_sph_select");
var $right_sph = $("#right_sph_select");

var $left_qty = $("#left_qty_select");
var $right_qty = $("#right_qty_select");

// Toggle UI
$toggle.on("change", function () {

    if ($(this).is(":checked")) {
        $(".both-eye-row").addClass("hidden");
        $(".split-eye-row").removeClass("hidden");
    } else {
        $(".both-eye-row").removeClass("hidden");
        $(".split-eye-row").addClass("hidden");
    }
});


// ---------- ADD TO CART ----------
$(document).on("click", ".custom__cart-drawer", function (e) {

    e.preventDefault();

    var $btn = $(this);
    $btn.prop("disabled", true);

    var $selectedRadio = $('input.custom__variants:checked');
    var variantId = $selectedRadio.attr("data-varid");

    if (!$toggle.is(":checked")) {

        ajaxAddToCart(
            variantId,
            parseInt($both_qty.val()) || 1,
            { "Both – Sphere": $both_sph.val() || "" },
            finishCart
        );

    } else {

        var leftQty = parseInt($left_qty.val()) || 0;
        var rightQty = parseInt($right_qty.val()) || 0;

        if (leftQty > 0) {

            ajaxAddToCart(
                variantId,
                leftQty,
                { "Left – Sphere": $left_sph.val() || "" },
                function () {

                    if (rightQty > 0) {

                        ajaxAddToCart(
                            variantId,
                            rightQty,
                            { "Right – Sphere": $right_sph.val() || "" },
                            finishCart
                        );

                    } else finishCart();
                }
            );

        } else if (rightQty > 0) {

            ajaxAddToCart(
                variantId,
                rightQty,
                { "Right – Sphere": $right_sph.val() || "" },
                finishCart
            );

        } else {
            finishCart();
        }
    }

    function finishCart() {
        refreshCartDrawer();
        $btn.prop("disabled", false);
    }

});


// ---------- AJAX ADD ----------
function ajaxAddToCart(variantId, quantity, properties, callback) {

    if (!variantId || quantity < 1) return;

    $.ajax({
        type: "POST",
        url: window.Shopify.routes.root + "cart/add.js",
        contentType: "application/json",
        data: JSON.stringify({
            id: variantId,
            quantity: quantity,
            properties: properties
        }),
        success: function () {
            if (callback) callback();
        }
    });
}


// ---------- REFRESH DRAWER ----------
function refreshCartDrawer() {

    $("cart-drawer").load(location.href + " #CartDrawer");
    $("#cart-icon-bubble").load(location.href + " #cart-icon-bubble");

    setTimeout(function () {
        $("cart-drawer.drawer").removeClass("is-empty").addClass("active");
    }, 800);
}

});
